// Global Instructions Rule Applied!
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { validateInteractionRecord, InteractionRecord } from '../../../state_manager/data_models/interaction_record.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface InteractionFilters {
  interaction_types?: string[]
  participant_ids?: string[]
  date_from?: string
  date_to?: string
}

/**
 * Fetch interactions for a specific deal
 * @param dealId - The deal ID
 * @param filters - Optional filters for interactions
 * @param accessToken - User's access token from Redux store
 * @returns Promise<InteractionRecord[]> Array of validated interactions
 */
export const fetchInteractionsForDeal = async (
  dealId: string,
  filters?: InteractionFilters,
  accessToken?: string | null,
  pagination?: { limit?: number; offset?: number }
): Promise<InteractionRecord[]> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const requestBody = {
      data: {
        filters: filters || {},
        limit: pagination?.limit ?? 50,
        offset: pagination?.offset ?? 0
      }
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEAL_INTERACTIONS.LIST, { dealId }), {
      method: API_ROUTES.DEAL_INTERACTIONS.LIST.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to fetch interactions')
    }

    const data = await response.json()

    const interactions = data.data || []

    return validateInteractionRecord(interactions, { isArray: true }) as InteractionRecord[]
  } catch (error) {
    console.error('Error fetching interactions:', error)
    throw error
  }
}

/**
 * Fetch interactions for a specific account
 * @param accountId - The account ID
 * @param filters - Optional filters for interactions
 * @param accessToken - User's access token from Redux store
 * @returns Promise<InteractionRecord[]> Array of validated interactions
 */
export const fetchInteractionsForAccount = async (
  accountId: string,
  filters?: InteractionFilters,
  accessToken?: string | null,
  pagination?: { limit?: number; offset?: number }
): Promise<InteractionRecord[]> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const requestBody = {
      data: {
        filters: filters || {},
        limit: pagination?.limit ?? 50,
        offset: pagination?.offset ?? 0
      }
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ACCOUNT_INTERACTIONS.LIST, { accountId }), {
      method: API_ROUTES.ACCOUNT_INTERACTIONS.LIST.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to fetch interactions')
    }

    const data = await response.json()

    const interactions = data.data || []

    return validateInteractionRecord(interactions, { isArray: true }) as InteractionRecord[]
  } catch (error) {
    console.error('Error fetching interactions:', error)
    throw error
  }
}

/**
 * Fetch interactions for a specific prospect
 * @param prospectId - The prospect ID
 * @param filters - Optional filters for interactions
 * @param accessToken - User's access token from Redux store
 * @returns Promise<InteractionRecord[]> Array of validated interactions
 */
export const fetchInteractionsForProspect = async (
  prospectId: string,
  filters?: InteractionFilters,
  accessToken?: string | null,
  pagination?: { limit?: number; offset?: number }
): Promise<InteractionRecord[]> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const requestBody = {
      data: {
        filters: filters || {},
        limit: pagination?.limit ?? 50,
        offset: pagination?.offset ?? 0
      }
    }

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.PROSPECT_INTERACTIONS.LIST, { prospectId }),
      {
        method: API_ROUTES.PROSPECT_INTERACTIONS.LIST.method,
        headers,
        body: JSON.stringify(requestBody)
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to fetch interactions')
    }

    const data = await response.json()

    const interactions = data.data || []

    return validateInteractionRecord(interactions, { isArray: true }) as InteractionRecord[]
  } catch (error) {
    console.error('Error fetching interactions:', error)
    throw error
  }
}

/**
 * Create a new interaction
 * @param recordId - The record ID (deal ID, account ID, or prospect ID)
 * @param interactionData - The interaction data
 * @param accessToken - User's access token from Redux store
 * @param recordType - The record type ('deal', 'company', or 'prospect')
 * @param accountId - Account ID (required for deal/prospect to link interaction to account)
 * @returns Promise<Object> Created interaction
 */
export const createInteraction = async (
  recordId: string,
  interactionData: unknown,
  accessToken?: string | null,
  recordType: 'deal' | 'company' | 'prospect' = 'deal',
  accountId?: string | null
): Promise<InteractionRecord> => {
  try {
    // Validate input before sending
    validateInteractionRecord(interactionData)

    // Ensure interactionData has the correct relationship field based on recordType
    const interactionDataWithRelationship = { ...(interactionData as Record<string, any>) }
    if (recordType === 'company') {
      interactionDataWithRelationship['interaction_accounts__account_id'] = recordId
      // Remove other relationship fields if present
      delete interactionDataWithRelationship['interaction_deals__deal_id']
      delete interactionDataWithRelationship['interaction_prospects__prospect_id']
    } else if (recordType === 'prospect') {
      interactionDataWithRelationship['interaction_prospects__prospect_id'] = recordId
      delete interactionDataWithRelationship['interaction_deals__deal_id']
      if (accountId) {
        interactionDataWithRelationship['interaction_accounts__account_id'] = accountId
      }
    } else {
      interactionDataWithRelationship['interaction_deals__deal_id'] = recordId
      delete interactionDataWithRelationship['interaction_prospects__prospect_id']
      if (accountId) {
        interactionDataWithRelationship['interaction_accounts__account_id'] = accountId
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERACTIONS.CREATE), {
      method: API_ROUTES.INTERACTIONS.CREATE.method,
      headers,
      body: JSON.stringify(interactionDataWithRelationship)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to create interaction')
    }

    const data = await response.json()

    if (data.success) {
      // Validate response after receiving
      return validateInteractionRecord(data.data)
    } else {
      throw new Error(data.message || 'Failed to create interaction')
    }
  } catch (error) {
    console.error('Error creating interaction:', error)
    throw error
  }
}

/**
 * Update an existing interaction
 * @param recordId - The record ID (deal ID, account ID, or prospect ID)
 * @param interactionId - The interaction ID
 * @param interactionData - The updated interaction data
 * @param accessToken - User's access token from Redux store
 * @param recordType - The record type ('deal', 'company', or 'prospect')
 * @returns Promise<Object> Updated interaction
 */
export const updateInteraction = async (
  recordId: string,
  interactionId: string,
  interactionData: unknown,
  accessToken?: string | null,
  recordType: 'deal' | 'company' | 'prospect' = 'deal'
): Promise<InteractionRecord> => {
  try {
    // Validate input before sending
    validateInteractionRecord(interactionData)

    // Ensure interactionData has the correct relationship field based on recordType
    const interactionDataWithRelationship = { ...(interactionData as Record<string, any>) }
    if (recordType === 'company') {
      interactionDataWithRelationship['interaction_accounts__account_id'] = recordId
      // Remove other relationship fields if present
      delete interactionDataWithRelationship['interaction_deals__deal_id']
      delete interactionDataWithRelationship['interaction_prospects__prospect_id']
    } else if (recordType === 'prospect') {
      interactionDataWithRelationship['interaction_prospects__prospect_id'] = recordId
      // Remove other relationship fields if present
      delete interactionDataWithRelationship['interaction_deals__deal_id']
      delete interactionDataWithRelationship['interaction_accounts__account_id']
    } else {
      interactionDataWithRelationship['interaction_deals__deal_id'] = recordId
      // Remove other relationship fields if present
      delete interactionDataWithRelationship['interaction_accounts__account_id']
      delete interactionDataWithRelationship['interaction_prospects__prospect_id']
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERACTIONS.UPDATE, { interactionId }), {
      method: API_ROUTES.INTERACTIONS.UPDATE.method,
      headers,
      body: JSON.stringify(interactionDataWithRelationship)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to update interaction')
    }

    const data = await response.json()

    if (data.success) {
      // Validate response after receiving
      return validateInteractionRecord(data.data)
    } else {
      throw new Error(data.message || 'Failed to update interaction')
    }
  } catch (error) {
    console.error('Error updating interaction:', error)
    throw error
  }
}

/**
 * Delete an interaction
 * @param recordId - The record ID (deal ID or account ID) - not used but kept for compatibility
 * @param interactionId - The interaction ID
 * @param accessToken - User's access token from Redux store
 * @returns Promise<void>
 */
export const deleteInteraction = async (
  recordId: string,
  interactionId: string,
  accessToken?: string | null
): Promise<void> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERACTIONS.DELETE, { interactionId }), {
      method: API_ROUTES.INTERACTIONS.DELETE.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to delete interaction')
    }

    const data = await response.json()

    // Check for success in either data.success or data.data.success
    const isSuccess = data.success === true || data.data?.success === true || data.status === 'success'
    if (!isSuccess) {
      throw new Error(data.message || data.data?.message || 'Failed to delete interaction')
    }
  } catch (error) {
    console.error('Error deleting interaction:', error)
    throw error
  }
}

/**
 * Delink an interaction from a module (hard delete relationship)
 * @param recordId - The record ID (deal ID, account ID, or prospect ID)
 * @param interactionId - The interaction ID
 * @param recordType - The record type ('deal', 'company', or 'prospect')
 * @param accessToken - User's access token from Redux store
 * @returns Promise<void>
 */
export const delinkInteraction = async (
  recordId: string,
  interactionId: string,
  recordType: 'deal' | 'company' | 'prospect',
  accessToken?: string | null
): Promise<void> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const requestBody = {
      record_id: recordId,
      record_type: recordType
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERACTIONS.DELINK, { interactionId }), {
      method: API_ROUTES.INTERACTIONS.DELINK.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to delink interaction')
    }

    const data = await response.json()

    // Check for success in either data.success or data.data.success
    const isSuccess = data.success === true || data.data?.success === true || data.status === 'success'
    if (!isSuccess) {
      throw new Error(data.message || data.data?.message || 'Failed to delink interaction')
    }
  } catch (error) {
    console.error('Error delinking interaction:', error)
    throw error
  }
}

/**
 * Fetch interaction links (accounts, deals, prospects)
 * @param interactionId - The interaction ID
 * @param accessToken - User's access token from Redux store
 * @returns Promise with account_ids, deal_ids, prospect_ids arrays (objects with id and label)
 */
export const fetchInteractionLinks = async (
  interactionId: string,
  accessToken?: string | null
): Promise<{
  account_ids: Array<{ id: string; label: string }>
  deal_ids: Array<{ id: string; label: string }>
  prospect_ids: Array<{ id: string; label: string }>
}> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERACTIONS.LINKS, { interactionId }), {
      method: API_ROUTES.INTERACTIONS.LINKS.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to fetch interaction links')
    }

    const data = await response.json()

    return {
      account_ids: data.data?.account_ids || [],
      deal_ids: data.data?.deal_ids || [],
      prospect_ids: data.data?.prospect_ids || []
    }
  } catch (error) {
    console.error('Error fetching interaction links:', error)
    throw error
  }
}

/**
 * Process interaction links (update accounts, deals, prospects)
 * @param data - Object with interaction_id, account_ids, deal_ids, prospect_ids
 * @param accessToken - User's access token from Redux store
 * @returns Promise<void>
 */
export const processInteractionLinks = async (
  data: {
    interaction_id: string
    account_ids: string[]
    deal_ids: string[]
    prospect_ids: string[]
  },
  accessToken?: string | null
): Promise<void> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERACTIONS.PROCESS), {
      method: API_ROUTES.INTERACTIONS.PROCESS.method,
      headers,
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to process interaction links')
    }

    const responseData = await response.json()

    const isSuccess = responseData.success === true || responseData.data?.success === true
    if (!isSuccess) {
      throw new Error(responseData.message || responseData.data?.message || 'Failed to process interaction links')
    }
  } catch (error) {
    console.error('Error processing interaction links:', error)
    throw error
  }
}
